import { observer } from 'mobx-react-lite';

import { CommandButton } from 'office-ui-fabric-react/lib/Button';
import { logUsage } from 'owa-analytics';
import { AttachmentFullViewState } from 'owa-attachment-full-data';
import { isSmimeAttachmentType } from 'owa-attachment-model-store';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import { downloadAttachmentFromUrl } from 'owa-attachment-user-operations';
import AttachmentWellViewState from 'owa-attachment-well-data/lib/schema/AttachmentWellViewState';
import { ClientAttachmentId, ClientItemId } from 'owa-client-ids';
import { isGroupSelected } from 'owa-group-utils';
import { getCurrentGroupSmtpAddress } from 'owa-groups-shared-store';
import loc from 'owa-localize';
import { getOwaCanaryCookie } from 'owa-service/lib/canary';
import { getScopedPath } from 'owa-url';
import * as React from 'react';
import { DOWNLOAD_ALL_RELATIVE_URL_TEMPLATE } from '../utils/constants';
import { downloadAllText } from './AttachmentWellBarView.locstring.json';
import AttachmentWellInfoView from './AttachmentWellInfoView';
import SaveAllToCloudView from './SaveAllToCloudView';

export interface AttachmentWellBarProps {
    attachmentWellViewState: AttachmentWellViewState;
    parentItemId: ClientItemId;
    isDraft: boolean;
    attachmentIdBeingPreviewed: ClientAttachmentId;
}

const styles = require('./AttachmentWellBarView.scss');
const classNames = require('classnames/bind').bind(styles);

// This component is responsible for showing the attachment well bar view.
export default observer(function AttachmentWellBarView(props: AttachmentWellBarProps) {
    const referentAttachmentsCount = React.useRef<number>();
    const classicAttachmentCount = React.useRef<number>();
    const downloadAllowed = React.useRef<boolean>();
    const getDownloadInfo = (attachments: AttachmentFullViewState[]) => {
        let attachment: AttachmentFullViewState;
        for (let i = 0; i < attachments.length; i++) {
            attachment = attachments[i];
            const attachmentModel = getAttachment(attachment.attachmentId);
            if (!attachmentModel || !attachmentModel.info || !attachmentModel.info.allowDownload) {
                downloadAllowed.current = false;
                if (!attachmentModel && !props.isDraft) {
                    // The reason we dont log in case the AttachmentModel is not available and it is draft is because
                    // when user forwards an email with attachment and then delete attachment (which deletes the model)
                    // and then tries sending it, compose keeps an older draft which still has the same attachment but
                    // the corresponding model was deleted causing this view to not have the AttachmentModel in the props.
                    // Rendering older draft even though a mail was forwarded is by design from compose and hence we need
                    // to handle this situation so that the view does not break.
                    // We log in all other cases
                    logUsage('AttachmentBarMissingModel', {
                        id: attachment.attachmentId.Id,
                    });
                }
            }
            if (!downloadAllowed.current) {
                break;
            }
            if (attachment.isCloudy) {
                referentAttachmentsCount.current++;
            } else {
                classicAttachmentCount.current++;
            }
        }
    };
    const onClickDownloadAllButton = (ev: any) => {
        const downloadAllRelativeUrl =
            DOWNLOAD_ALL_RELATIVE_URL_TEMPLATE + encodeURIComponent(props.parentItemId.Id);
        const groupString = isGroupSelected() ? getCurrentGroupSmtpAddress() + '/' : '';
        const downloadAllUrl = `${getScopedPath(
            '/owa/'
        )}/${groupString}${downloadAllRelativeUrl}&X-OWA-CANARY=${getOwaCanaryCookie()}`;
        downloadAttachmentFromUrl(downloadAllUrl, false);
        logUsage('AttachmentWellDownloadAll', [
            props.attachmentWellViewState.docViewAttachments.length +
                props.attachmentWellViewState.imageViewAttachments.length,
            props.attachmentWellViewState.totalClassicAttachmentSize,
        ]);
    };
    const hasMultipleAttachments =
        props.attachmentWellViewState.imageViewAttachments.length +
            props.attachmentWellViewState.docViewAttachments.length >
        1;
    referentAttachmentsCount.current = 0;
    classicAttachmentCount.current = 0;
    downloadAllowed.current = true;
    // Since we ensure that smime attachment is always of doc type, no need to check for imageViewAttachments
    const hasSmimeAttachment = isSmimeAttachmentPresent(
        props.attachmentWellViewState.docViewAttachments
    );
    getDownloadInfo(props.attachmentWellViewState.imageViewAttachments);
    getDownloadInfo(props.attachmentWellViewState.docViewAttachments);
    const shouldShowDownloadAll: boolean =
        downloadAllowed.current &&
        classicAttachmentCount.current > 1 &&
        referentAttachmentsCount.current === 0 &&
        !hasSmimeAttachment;
    return (
        <span className={styles.attachmentWellBar}>
            {hasMultipleAttachments && (
                <AttachmentWellInfoView
                    attachmentWellViewState={props.attachmentWellViewState}
                    attachmentIdBeingPreviewed={props.attachmentIdBeingPreviewed}
                />
            )}
            {shouldShowDownloadAll && (
                <CommandButton
                    className={classNames(styles.barButton, styles.barButtonHover)}
                    onClick={onClickDownloadAllButton}>
                    {loc(downloadAllText)}
                </CommandButton>
            )}
            {!hasSmimeAttachment && (
                <SaveAllToCloudView
                    attachmentWellViewState={props.attachmentWellViewState}
                    parentItemId={props.parentItemId}
                />
            )}
        </span>
    );
});

function isSmimeAttachmentPresent(attachments: AttachmentFullViewState[]): boolean {
    return attachments.some(attachment => {
        const attachmentModel = getAttachment(attachment.attachmentId);
        return attachmentModel && isSmimeAttachmentType(attachmentModel.model);
    });
}
